import { useEffect } from 'react';
import {
  disconnectVoiceClient,
  endAgentSession,
  updateAccessToken,
  VoicePreference,
} from '../../services/cxone-sdk.service';
import {
  getDefaultWebRtcVoicePreference,
  shouldShowVoicePreferenceSelector,
} from '../../utils/config';
import { logger } from '../../utils/logger';
import { AuthUser } from '../../services/api';
import { CXoneState } from '../../types';
import { CxoneSessionRefs } from './types';

interface UseCxoneLifecycleOptions {
  isAuthenticated: boolean;
  accessToken: string | null;
  user: AuthUser | null;
  needsVoicePreference: boolean;
  isInitialized: boolean;
  refs: CxoneSessionRefs;
  initializeWithVoicePreference: (preference: VoicePreference) => Promise<void>;
  setNeedsVoicePreference: React.Dispatch<React.SetStateAction<boolean>>;
  resetConnectionState: () => void;
  resetRecoveryState: () => void;
  setState: React.Dispatch<React.SetStateAction<CXoneState>>;
}

export function useCxoneLifecycle({
  isAuthenticated,
  accessToken,
  user,
  needsVoicePreference,
  isInitialized,
  refs,
  initializeWithVoicePreference,
  setNeedsVoicePreference,
  resetConnectionState,
  resetRecoveryState,
  setState,
}: UseCxoneLifecycleOptions) {
  useEffect(() => {
    if (isAuthenticated && accessToken && user?.cxoneAgentId) {
      if (!refs.initializedRef.current && !refs.initializingRef.current && !needsVoicePreference) {
        if (shouldShowVoicePreferenceSelector()) {
          logger.debug('[CXone] User authenticated, showing voice preference selector...');
          setNeedsVoicePreference(true);
        } else {
          logger.debug(
            '[CXone] User authenticated, auto-connecting with WebRTC (voice selector disabled)...'
          );
          void initializeWithVoicePreference(getDefaultWebRtcVoicePreference());
        }
      }
    }
  }, [
    isAuthenticated,
    accessToken,
    user?.cxoneAgentId,
    needsVoicePreference,
    initializeWithVoicePreference,
    setNeedsVoicePreference,
    refs,
  ]);

  useEffect(() => {
    if (isInitialized && accessToken && refs.previousTokenRef.current !== accessToken) {
      logger.debug('[CXone] Access token changed, updating SDK...');
      updateAccessToken(accessToken).catch((error) => {
        logger.error('[CXone] Failed to update token:', error);
      });
      refs.previousTokenRef.current = accessToken;
    }
  }, [accessToken, isInitialized, refs]);

  useEffect(() => {
    if (isAuthenticated && user && !user.cxoneAgentId) {
      logger.warn('[CXone] No CXone agent ID mapped for user');
      setState({
        isInitialized: false,
        isConnecting: false,
        isConnected: false,
        connectionError: 'No CXone agent ID configured. Please contact your administrator.',
        connectionProgress: null,
      });
    }
  }, [isAuthenticated, user, setState]);

  useEffect(() => {
    if (!isAuthenticated && refs.initializedRef.current) {
      logger.lifecycle('[CXone] Disconnecting session after logout');
      disconnectVoiceClient();
      endAgentSession().catch((err) => {
        logger.error('[CXone] Error ending session on logout:', err);
      });
      resetConnectionState();
      resetRecoveryState();
      logger.lifecycle('[CXone] Session disconnected after logout');
    }
  }, [isAuthenticated, refs, resetConnectionState, resetRecoveryState]);
}
